interface Pt {
  date: string;
  value: number | null;
}

/**
 * A dependency-free chart for trend series that may contain gaps. Null values
 * break the line (we never interpolate across missing days — that would invent
 * data), and real points are dotted. Bars are used for small discrete counts
 * (e.g. workouts/week); otherwise a line with a soft fill.
 */
export function MetricChart({
  points,
  unit = "",
  height = 150,
  color = "var(--accent)",
  mode = "line"
}: {
  points: Pt[];
  unit?: string;
  height?: number;
  color?: string;
  mode?: "line" | "bar";
}) {
  const W = 320;
  const H = height;
  const padX = 10;
  const padY = 18;

  const real = points.filter((p) => p.value !== null) as { date: string; value: number }[];
  if (real.length === 0) {
    return <div className="dim text-sm center" style={{ padding: "var(--space-6)" }}>No data in this range yet.</div>;
  }

  const values = real.map((p) => p.value);
  let min = Math.min(...values, mode === "bar" ? 0 : Math.min(...values));
  let max = Math.max(...values);
  if (mode === "bar") min = 0;
  if (min === max) {
    max += 1;
    min -= mode === "bar" ? 0 : 1;
  }
  const n = points.length;
  const x = (i: number) => (n <= 1 ? W / 2 : padX + (i / (n - 1)) * (W - padX * 2));
  const y = (v: number) => padY + (1 - (v - min) / (max - min)) * (H - padY * 2);

  const lastReal = real[real.length - 1]!;
  const firstReal = real[0]!;
  const gradId = `mg${Math.random().toString(36).slice(2, 8)}`;

  // Build line segments that break across null gaps.
  const segments: string[] = [];
  let current = "";
  points.forEach((p, i) => {
    if (p.value === null) {
      if (current) { segments.push(current); current = ""; }
      return;
    }
    current += `${current ? " L" : "M"} ${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`;
  });
  if (current) segments.push(current);

  const barW = Math.max(3, (W - padX * 2) / Math.max(1, n) - 3);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }} role="img">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>

      {mode === "bar" ? (
        points.map((p, i) =>
          p.value === null ? null : (
            <rect
              key={i}
              x={x(i) - barW / 2}
              y={y(p.value)}
              width={barW}
              height={Math.max(0, H - padY - y(p.value))}
              rx={2}
              fill={color}
              opacity={0.85}
            />
          )
        )
      ) : (
        <>
          {segments.length === 1 && (
            <path d={`${segments[0]} L ${x(points.length - 1).toFixed(1)} ${(H - padY).toFixed(1)} L ${x(0).toFixed(1)} ${(H - padY).toFixed(1)} Z`} fill={`url(#${gradId})`} opacity={0.6} />
          )}
          {segments.map((d, i) => (
            <path key={i} d={d} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          ))}
          {real.map((p, i) => {
            const idx = points.findIndex((q) => q.date === p.date);
            return <circle key={i} cx={x(idx)} cy={y(p.value)} r={2.4} fill={color} opacity={0.85} />;
          })}
        </>
      )}

      <text x={padX} y={14} fontSize={11} fill="var(--text-tertiary)">{firstReal.value}{unit}</text>
      <text x={W - padX} y={14} fontSize={11} fill="var(--text-primary)" textAnchor="end" fontWeight={600}>{lastReal.value}{unit}</text>
    </svg>
  );
}
